import {useRef, useCallback, useEffect, type PointerEvent as ReactPointerEvent} from 'react';
import moment from 'moment';
import {useOperationsContext} from '../../../../../context/OperationsContextProvider';
import { getReservationInfo, isReservationAnnouncement } from '../../../../../utils';
import useLongPress from '../../../../../hooks/useLongPress';

const keysMap = {
  allDayByDate: 'כל היום',
  recurring: 'מחזורי',
  byDate: '',
};

function ScheduledCalendar() {
  const {
    calendarData,
    setSelectedAnnouncementId,
    setShowAnnouncementViewModal,
    selectedCalendarEvent,
    setSelectedCalendarEvent,
  } = useOperationsContext();

  const now = moment();
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const longPressEventRef = useRef<any>(null);
  const didLongPressRef = useRef(false);

  const clearSelectedCalendarSelection = useCallback(() => {
    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    setSelectedCalendarEvent(null);
  }, [setSelectedCalendarEvent]);

  useEffect(() => {
    if (!selectedCalendarEvent) return;

    const handleDocumentPointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[data-calendar-event-box="true"]')) return;
      if (target.closest('[data-calendar-selection-keep="true"]')) return;
      if (target.closest('.MuiMenu-root, .MuiPopover-root')) return;
      if (target.closest('.fixed.z-50')) return;

      clearSelectedCalendarSelection();
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown, true);

    return () => {
      document.removeEventListener('pointerdown', handleDocumentPointerDown, true);
    };
  }, [clearSelectedCalendarSelection, selectedCalendarEvent]);

  const handleSingleClick = useCallback(
    (event: any) => {
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
      setSelectedCalendarEvent(event);
      clickTimeoutRef.current = setTimeout(() => {
        if (!event?.source?._id) return;
        setSelectedAnnouncementId(event.source._id);
        setShowAnnouncementViewModal(true);
      }, 250);
    },
    [
      setSelectedAnnouncementId,
      setSelectedCalendarEvent,
      setShowAnnouncementViewModal,
    ],
  );

  const handleLongPress = useCallback(
    () => {
      if (!longPressEventRef.current) return;
      didLongPressRef.current = true;
      setSelectedCalendarEvent(longPressEventRef.current);
    },
    [setSelectedCalendarEvent],
  );
  const longPressHandlers = useLongPress<HTMLButtonElement>(handleLongPress);

  const getLongPressProps = useCallback(
    (event: any) => ({
      ...longPressHandlers,
      onPointerDown: (pointerEvent: ReactPointerEvent<HTMLButtonElement>) => {
        didLongPressRef.current = false;
        longPressEventRef.current = event;
        longPressHandlers.onPointerDown(pointerEvent);
      },
    }),
    [longPressHandlers],
  );

  const renderEvent = (item: any, isCurrent: boolean, isAllDay = false) => {
    const isSelected = selectedCalendarEvent?.id === item.id;
    const isReservation = isReservationAnnouncement(item?.source);
    const { zoneColor, label: reservationLabel } = isReservation
      ? getReservationInfo(item.source)
      : { zoneColor: undefined, label: "" };
    return (
      <button
        key={item.id}
        data-calendar-event-box="true"
        onClick={e => {
          e.stopPropagation();
          if (didLongPressRef.current) return;
          handleSingleClick(item);
        }}
        {...getLongPressProps(item)}
        className="w-full border-b-2 border-b-white flex flex-col gap-1 "
      >
        <div
          className={`py-2 rounded-[8px] px-2 flex items-center gap-3 hover:bg-icyBlue transition-colors duration-150 ${
            isSelected
              ? "border-2 border-linkBlue"
              : isCurrent
                ? "border border-lightDarkGray"
                : "border border-icyBlue"
          } ${isCurrent ? "bg-[#EBEBEB]" : "bg-paleAqua"}`}
          style={isReservation && zoneColor && !isCurrent
              ? { backgroundColor: zoneColor, borderColor: zoneColor }
              : undefined}
        >
          {!isAllDay && (
            <p
              className={`text-[14px] ${
                isCurrent
                  ? "font-medium text-grayText"
                  : "font-semibold text-black"
              }`}
            >
              {item.start} - {item.end}
            </p>
          )}
          <p
            className={`text-[14px] flex-1 text-right ${
              isCurrent
                ? "font-medium text-grayText"
                : "font-semibold text-black"
            }`}
          >
            {isReservation ? reservationLabel : item.title}
          </p>
        </div>

        {isCurrent && <div className="w-full h-[3px] bg-raspberryRed" />}
      </button>
    );
  };

  const renderByDateGroup = (data: any, label: string, allDay = false) =>
    data &&
    Object.keys(data).length > 0 && (
      <div>
        {label && <h3 className="text-lg font-semibold">{label}</h3>}
        {Object.entries(data).map(([date, items], idx) => {
          const isToday = date === now.format('YYYY/MM/DD');
          const eventItems = items as any[];
          return (
            <div key={date} className="w-full flex flex-col gap-1">
              <p className="m-0 text-[18px] font-medium text-darkerGray">
                {moment(date, 'YYYY/MM/DD').format('dddd, MMMM DD')}
              </p>
              {eventItems.map(item =>
                renderEvent(
                  item,
                  isToday &&
                    item.start &&
                    item.end &&
                    item.start <= now.format('HH:mm') &&
                    item.end >= now.format('HH:mm'),
                  allDay,
                ),
              )}
            </div>
          );
        })}
      </div>
    );

  const renderRecurring = (items: any[]) =>
    items?.length > 0 && (
      <div>
        <h3 className="text-lg font-semibold">{keysMap.recurring}</h3>
        {items.map(item =>
          renderEvent(
            item,
            item.start <= now.format('HH:mm') &&
              item.end >= now.format('HH:mm'),
          ),
        )}
      </div>
    );

  return (
    <>
      <div className="flex-1 min-h-0 py-3 px-4 flex flex-col gap-3">
        {renderByDateGroup(
          calendarData.allDayByDate,
          keysMap.allDayByDate,
          true,
        )}
        {renderRecurring(calendarData.recurring)}
        {renderByDateGroup(calendarData.byDate, keysMap.byDate)}
      </div>

    </>
  );
}

export default ScheduledCalendar;
